import React, { useEffect, useState } from "react";
import { api, errMsg, getUser, setAuth, getToken } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { toast } from "sonner";
import { KeyRound, User as UserIcon, Save, Loader2, Trash2, AlertTriangle } from "lucide-react";
import MyntraIntegrationCard from "../components/MyntraIntegrationCard";

export default function SellerProfile() {
  const cached = getUser() || {};
  const [profile, setProfile] = useState({
    name: cached.name || "",
    phone: cached.phone || "",
    company: cached.company || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ old_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  // Reset-all-data state — single-step confirm input keeps the UI calm
  // but still requires the seller to type "delete" before we wipe their
  // uploads, reports, SKU master, competitor snapshots etc.
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  async function onResetAllData() {
    if (resetConfirm.trim().toLowerCase() !== "delete") {
      toast.error('Please type "delete" to confirm');
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.delete("/seller/data/clear");
      toast.success(
        `Cleared ${data.uploads_deleted || 0} uploads · ${data.reports_deleted || 0} reports · ${data.sku_master_deleted || 0} SKUs`
      );
      setResetConfirm("");
    } catch (err) {
      toast.error(errMsg(err, "Failed to clear data"));
    } finally {
      setResetting(false);
    }
  }

  async function onSaveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/seller/profile", profile);
      setAuth(getToken(), data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errMsg(err, "Failed to update"));
    } finally {
      setSavingProfile(false);
    }
  }

  async function onChangePwd(e) {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) return toast.error("Passwords do not match");
    if (pwd.new_password.length < 6) return toast.error("Minimum 6 characters");
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        old_password: pwd.old_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setSavingPwd(false);
    }
  }

  return (
    <AppShell role="seller">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Profile</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">Your account.</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={onSaveProfile} className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="profile-form">
          <div className="flex items-center gap-2 mb-4">
            <UserIcon size={16} className="text-[#4F46E5]" />
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Profile details</div>
          </div>
          {[
            ["name", "Full name"],
            ["phone", "Phone"],
            ["company", "Company"],
          ].map(([k, label]) => (
            <div key={k} className="mb-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</label>
              <input
                value={profile[k] || ""}
                onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-white/[0.08] rounded-md focus:outline-none focus:border-[#4F46E5]"
                data-testid={`profile-${k}`}
              />
            </div>
          ))}
          <div className="mb-4">
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email (read-only)</label>
            <input value={cached.email || ""} disabled className="mt-1 w-full px-3 py-2 border border-white/[0.08] rounded-md bg-white/[0.02] text-slate-500" />
          </div>
          <button type="submit" disabled={savingProfile} className="btn-primary w-full" data-testid="profile-save-btn">
            {savingProfile ? <Loader2 size={14} className="inline animate-spin" /> : <><Save size={14} className="inline mr-1" /> Save changes</>}
          </button>
        </form>

        <form onSubmit={onChangePwd} className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="change-password-form">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} className="text-[#4F46E5]" />
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Change password</div>
          </div>
          {[
            ["old_password", "Current password"],
            ["new_password", "New password"],
            ["confirm", "Confirm new password"],
          ].map(([k, label]) => (
            <div key={k} className="mb-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</label>
              <input
                type="password"
                required
                value={pwd[k]}
                onChange={(e) => setPwd({ ...pwd, [k]: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-white/[0.08] rounded-md focus:outline-none focus:border-[#4F46E5]"
                data-testid={`pwd-${k}`}
              />
            </div>
          ))}
          <button type="submit" disabled={savingPwd} className="btn-primary w-full" data-testid="pwd-submit-btn">
            {savingPwd ? <Loader2 size={14} className="inline animate-spin" /> : "Update password"}
          </button>
        </form>
      </div>

      {/* Myntra Partner API integration — paste creds, push catalogs in one click */}
      <MyntraIntegrationCard />

      {/* Reset All Data — inline tab (no scary modal). Type "delete" + click
          and we wipe every upload, report, SKU and snapshot for THIS seller.
          Account itself stays — same login still works. */}
      <div
        className="mt-8 rounded-2xl border p-6"
        style={{ borderColor: "#FFC9D5", background: "#FFF5F8" }}
        data-testid="reset-all-data-section"
      >
        <div className="flex items-center gap-2 mb-2">
          <Trash2 size={16} style={{ color: "#FF3F6C" }} />
          <div className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: "#FF3F6C" }}>
            Delete All Data
          </div>
        </div>
        <p className="text-sm mb-4" style={{ color: "#535665" }}>
          Wipes every uploaded sales file, report, SKU master record and
          competitor snapshot tied to this account. Your login, plan and
          credits stay intact — you can start fresh with new uploads.
          Action is permanent and can't be undone.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            value={resetConfirm}
            onChange={(e) => setResetConfirm(e.target.value)}
            placeholder='Type "delete" to confirm'
            className="flex-1 max-w-sm px-3 py-2 rounded-md border text-sm"
            style={{ borderColor: "#FFC9D5", color: "#282C3F", background: "#FFFFFF" }}
            data-testid="reset-confirm-input"
          />
          <button
            onClick={onResetAllData}
            disabled={resetting || resetConfirm.trim().toLowerCase() !== "delete"}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "#FF3F6C", color: "#FFFFFF" }}
            data-testid="reset-all-btn"
          >
            {resetting ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
            {resetting ? "Wiping…" : "Delete all my data"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
